import styled from "@emotion/styled";
import { Autocomplete, TextField } from "@mui/material";
import { useField } from "formik";
import { useCallback, useMemo, useState, type ComponentProps, type ReactNode } from "react";

import { Tooltip } from "../../dataDisplay";
import { DEFAULT_INPUT_HEIGHT } from "../../utilities/constants";
import { AdornmentIcon } from "../AdornmentIcon";
import { useFieldContext } from "../FormFieldContext";
import { useCommitChange, useFieldLabelAndDescription } from "./helpers";
import type { AutocompleteOption } from "./types";

const StyledTextField = styled(TextField)`
  input {
    height: ${DEFAULT_INPUT_HEIGHT}px;
    box-sizing: border-box;
  }
`;

// see comments on selected state
const multiple = true;
const disableClearable = true;
type InnerComponent = typeof Autocomplete<AutocompleteOption, typeof multiple, typeof disableClearable, boolean>;

type FieldProps = Omit<TextSuggestionsProps, "label" | "value" | "setValue">;

export const FieldTextSuggestions = ({ options, icon, tooltip, ...props }: FieldProps) => {
  const commitChange = useCommitChange();

  const { name } = useFieldContext();
  const [label] = useFieldLabelAndDescription(name);

  const [{ value }, _, { setValue }] = useField<string | undefined>(name);

  const handleSetValue = useCallback(
    (newValue: string | undefined) => {
      // Commit after the value settles so the submit sees the fresh value.
      setValue(newValue)
        .then(() => commitChange())
        .catch(console.warn);
    },
    [commitChange, setValue],
  );

  return (
    <TextSuggestions
      {...props}
      label={label}
      value={value ?? ""}
      setValue={handleSetValue}
      options={options}
      icon={icon}
      tooltip={tooltip}
    />
  );
};

const optionMatchesInput = (option: AutocompleteOption, input: string) => {
  return option.label.toLowerCase() === input.toLowerCase() || option.value.toLowerCase() === input.toLowerCase();
};

export type TextSuggestionsProps = Omit<
  ComponentProps<InnerComponent>,
  // Inner props set by TextSuggestions
  | "autoSelect"
  | "openOnFocus"
  | "blurOnSelect"
  | "disableClearable"
  | "multiple"
  | "value"
  | "inputValue"
  | "onChange"
  | "onInputChange"
  | "options"
  | "renderInput"
  | "renderOption"
  // other props that don't make sense with the other options
  | "renderValue" // used when multiple true
  | "defaultValue" // not used if controlled, e.g. value set. Not valid to change this dynamically.
> & {
  // Current committed value. Controlled component, so must be provided.
  value: string;
  // Update the committed value. Disabled if not provided.
  setValue?: (value: string) => void;
  // Label displayed above the input
  label?: string | undefined;

  // Check if the value is valid to commit:
  // Returns:
  // - true is the value is valid
  // - false if the value is invalid, but no error message is needed
  // - a string error message if the value is invalid
  // If provided, and the result is false or a string, the value will not be committed with setValue().
  // This validation only applies to free-input values, not to selected options, including options selected
  // by matching the input (which commits the exact option value even if it matched the label).
  validation?: (value: string) => string | boolean;

  // Attempt to coerce the value to a valid one.
  coerceValue?: (value: string) => string;

  // List of options to select from.
  // - value is the committed value
  // - label is displayed in the drop-down, and initializes the input when selected
  // When the user edits the input, the first option with a case-insensitive value or label match will be selected,
  // as if the user selected it from the drop-down.
  // This implies there are essentially two classes of values than can be entered:
  // - translatable values that are displayed to the user in the drop-down, e.g. "Left", or
  // - free-input raw values that can be type in, e.g. "60%"
  options: AutocompleteOption[];

  // Displayed when hovering over the input
  tooltip?: string;

  // Icon displayed to the left of the input
  icon?: ReactNode;
};

export const TextSuggestions = ({
  value,
  setValue,
  label,
  validation,
  coerceValue,
  options,
  tooltip,
  icon,
  freeSolo = true,
  ...props
}: TextSuggestionsProps) => {
  // Autocomplete doesn't like readonly arrays for value.
  // Forcing using an array as Autocomplete complains if you give it an option not
  // in the list, and can crash if you change multiple while it's mounted.
  // Workaround by always using multiple, which is OK as we use onChange details.option
  // to actually select the values, so you can only ever actually select one.

  // Only store the editing state - when user is actively typing
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Derive the input value: use editing value if editing, otherwise derive from props
  const inputValue =
    editingValue !== null ? editingValue : (options.find((item) => optionMatchesInput(item, value))?.label ?? value);

  // Derive the selected option from the current input value
  const selected = useMemo(() => {
    const option = options.find((item) => optionMatchesInput(item, inputValue));
    return option ? [option] : [];
  }, [options, inputValue]);

  type InnerProps = Required<ComponentProps<InnerComponent>>;

  const createHandleClose = useCallback((): InnerProps["onClose"] => {
    let commitOnBlur = true;

    return (_event, reason) => {
      setEditingValue(null);
      setErrorMessage(null);

      if (reason === "removeOption" || reason === "selectOption") {
        // do not commit the free value on blur
        commitOnBlur = false;
        return;
      }

      if (reason !== "escape" && commitOnBlur && freeSolo) {
        // commit any editing value on closing the drop-down
        if (editingValue && (validation?.(editingValue) ?? true) === true) {
          setValue?.(editingValue);
        } else if (editingValue && coerceValue) {
          const coercedValue = coerceValue(editingValue);

          if (coercedValue !== editingValue) {
            setValue?.(coercedValue);
          }
        }
      }
    };
  }, [coerceValue, editingValue, freeSolo, setValue, validation]);

  const handleChange = useCallback<InnerProps["onChange"]>(
    (_event, _newValues, reason, details) => {
      // "removeOption" happens when selecting an already selected option.
      // That should still select the value as it can e.g. change multiple values
      // over a rich-text selection to a single option.
      if (reason === "selectOption" || reason === "removeOption") {
        // the user just selected an item from the drop-down, set to that option directly.
        const selectedOption = details!.option;
        setValue?.(selectedOption.value);
      }
    },
    [setValue],
  );

  const handleInputChange = useCallback<InnerProps["onInputChange"]>(
    (_event, newValue, reason) => {
      if (reason !== "input") return;

      setEditingValue(newValue); // update editing state
      setErrorMessage(null);

      const result = validation?.(newValue) ?? true;

      if (result === true) return;

      const coercedValue = coerceValue?.(newValue) ?? newValue;

      if (coercedValue === newValue) {
        setErrorMessage(result === false ? "" : result);
      }
    },
    [coerceValue, validation],
  );

  return (
    <Autocomplete<AutocompleteOption, typeof multiple, typeof disableClearable, boolean>
      {...props}
      freeSolo={freeSolo}
      openOnFocus
      blurOnSelect
      disableClearable={disableClearable}
      multiple={multiple}
      disabled={!setValue}
      value={selected}
      inputValue={inputValue}
      onClose={createHandleClose()}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      isOptionEqualToValue={(option, selectedValue) =>
        option.value === (typeof selectedValue === "string" ? selectedValue : selectedValue.value)
      }
      renderInput={(params) => {
        return (
          <StyledTextField
            {...params}
            label={label}
            variant="standard"
            error={errorMessage !== null}
            helperText={errorMessage}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: icon ? (
                  <Tooltip title={tooltip} placement="top" enterDelay={1000}>
                    <AdornmentIcon>{icon}</AdornmentIcon>
                  </Tooltip>
                ) : undefined,
              },
              htmlInput: {
                ...params.slotProps.htmlInput,
                style: { minWidth: 0 },
              },
            }}
          />
        );
      }}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option.value}>
          {option.label}
        </li>
      )}
      renderValue={() => null}
      filterOptions={noOptionsFiltering}
    />
  );
};

const noOptionsFiltering = (options: AutocompleteOption[]) => options;
